import type { FunctionComponent, ReactElement } from 'react'
import { cloneElement, useContext, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  PopoverCloseTrigger,
  useBreakpointValue,
  Drawer,
  Portal,
  CloseButton,
} from '@chakra-ui/react'
import {
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/ui/popover'
import BibleSelectorPanelCloser from './BibleSelectorPanelCloser'
import { BibleSelectorContext } from './BibleSelectorContextProvider'
import { SelectedValueContext } from './BibleSelector'
import { isSelectedValueComplete } from './utils'
import { useT, useUiSizeClassName } from '@/features/settings/settingsSlice'

interface BibleDropDownProps {
  label: String
  children: ReactElement
  onClose?: Function
}

export enum SelectType {
  Book,
  Chapter,
  Verse,
}

export type SelectValue = {
  book?: string
  chapter?: number
  verse?: number
}

export interface BibleSelectorProps {
  selectType: SelectType
  onClose?: Function
}

function isNonDesktopEnvironment(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined')
    return false

  const ua = navigator.userAgent || ''
  // 常见移动/平板 UA 标志
  const mobileUaRe =
    /Mobi|Android|iPhone|Windows Phone|webOS|BlackBerry|Opera Mini|Mobile/i

  // 触摸能力（包括触屏笔记本和平板）
  const hasTouch =
    'ontouchstart' in window ||
    (navigator as any).maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0

  // iPadOS 13+ 在 UA 中可能看起来像 Mac；用 platform + maxTouchPoints 进一步判断
  const isIpad =
    /iPad/.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1)

  return mobileUaRe.test(ua) || hasTouch || isIpad
}

const BibleDropDown: FunctionComponent<
  BibleDropDownProps & BibleSelectorProps
> = ({ label, children, onClose, selectType, ...selectorProps }) => {
  const { selected } = useContext(SelectedValueContext)!
  const { showVerseSelector } = useContext(BibleSelectorContext)!
  const closeTriggerRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (
      closeTriggerRef.current &&
      isSelectedValueComplete(selected!, showVerseSelector)
    ) {
      closeTriggerRef.current.click()
    }
  }, [selected, showVerseSelector])
  // 原来的响应式判断：
  const responsiveMobile = useBreakpointValue({ base: true, maxContent: false })
  // 新的“不是电脑环境”判断
  const nonDesktop = isNonDesktopEnvironment()
  const isMobile = Boolean(responsiveMobile) || nonDesktop // 响应式布局

  const popover = (
    <PopoverRoot
      positioning={{ placement: 'bottom-start' }}
      modal
      onExitComplete={() => {
        onClose?.()
      }}
      unmountOnExit
    >
      <PopoverTrigger asChild>
        <Button
          // @ts-ignore
          fontSize={useUiSizeClassName('sm', 'button')}
          variant="plain"
          focusRing={'none'}
          color={'whiteAlpha.800'}
          _hover={{
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            textDecorationColor: 'black.400',
            textDecorationThickness: '2px',
            color: 'white',
          }}
        >
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent width={'unset'}>
        {/* 取消Chakra UI的Popover的默认宽度 */}
        <PopoverArrow />
        <PopoverBody>
          <Box position={'relative'}>
            <PopoverCloseTrigger ref={closeTriggerRef}>
              <BibleSelectorPanelCloser />
            </PopoverCloseTrigger>
            {cloneElement(children, selectorProps)}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  )
  const drawer = (
    <Drawer.Root size={'sm'} onExitComplete={() => onClose?.()} unmountOnExit>
      <Drawer.Trigger asChild>
        <Button
          // @ts-ignore
          fontSize={useUiSizeClassName('sm', 'button')}
          variant="plain"
          focusRing={'none'}
          color={'whiteAlpha.800'}
          _hover={{
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            textDecorationColor: 'black.400',
            textDecorationThickness: '2px',
            color: 'white',
          }}
        >
          {label}
        </Button>
      </Drawer.Trigger>
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content animationDuration={'0.1s'}>
            <Drawer.Header>
              <Drawer.Title fontSize={useUiSizeClassName('md', 'text')}>
                {useT(['选择书卷和章', '選擇書卷和章'])}
              </Drawer.Title>
            </Drawer.Header>
            <Drawer.Body display={'flex'} flexDirection={'column'}>
              {cloneElement(children, {
                ...selectorProps,
                tabView: selectType === SelectType.Book ? 'books' : 'chapters',
              })}
            </Drawer.Body>

            <Drawer.CloseTrigger asChild ref={closeTriggerRef}>
              <CloseButton size="sm" />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
  return isMobile ? drawer : popover
}

export default BibleDropDown
